import { z } from 'zod';
import { idSchema } from './common.js';
import { dateOnlySchema, mealSlotSchema, timeOfDaySchema } from './plans.js';
/**
 * Household routine, calendars and reminders (D27/D28): usual meal times,
 * weekly busy blocks, imported iCal feeds, the read-only export feed, and
 * per-meal reminders (thawing).
 */
/** 0 = Sunday … 6 = Saturday (JS `Date#getDay`). */
export const weekdaySchema = z.number().int().min(0).max(6);
// ------------------------------------------------------------- meal times --
export const mealTimeDtoSchema = z.object({
    weekday: weekdaySchema,
    slot: mealSlotSchema,
    serveAt: timeOfDaySchema,
    /** Where this meal is usually eaten (D32); null/omitted = home. */
    placeId: idSchema.nullable().optional(),
    /** Who usually eats it (household person ids); empty/omitted = everyone. */
    attendeePersonIds: z.array(idSchema).max(50).optional(),
});
/** GET /api/v1/households/me/meal-times */
export const mealTimesResponseSchema = z.object({
    mealTimes: z.array(mealTimeDtoSchema),
    /** True when the household has saved nothing yet: these are the defaults (dinner 18:00 daily). */
    isDefault: z.boolean(),
});
/** PUT /api/v1/households/me/meal-times — replaces the whole week. */
export const replaceMealTimesRequestSchema = z.object({
    mealTimes: z
        .array(mealTimeDtoSchema)
        .max(28)
        .refine((rows) => new Set(rows.map((r) => `${r.weekday}:${r.slot}`)).size === rows.length, 'Each weekday and meal may appear only once'),
});
// ------------------------------------------------------------ busy blocks --
export const busyBlockDtoSchema = z.object({
    id: idSchema,
    /** Member this belongs to (display only); null = whole household. */
    userId: idSchema.nullable(),
    label: z.string(),
    weekday: weekdaySchema,
    startTime: timeOfDaySchema,
    /** Not after `startTime` = runs past midnight. */
    endTime: timeOfDaySchema,
});
export const busyBlocksResponseSchema = z.object({ busyBlocks: z.array(busyBlockDtoSchema) });
/** POST /api/v1/households/me/busy-blocks */
export const createBusyBlockRequestSchema = z
    .object({
    label: z.string().trim().min(1).max(120),
    weekday: weekdaySchema,
    startTime: timeOfDaySchema,
    endTime: timeOfDaySchema,
    userId: idSchema.nullable().optional(),
})
    .refine((b) => b.startTime !== b.endTime, {
    message: 'End time must differ from start time',
    path: ['endTime'],
});
/** PATCH /api/v1/households/me/busy-blocks/:id */
export const updateBusyBlockRequestSchema = z.object({
    label: z.string().trim().min(1).max(120).optional(),
    weekday: weekdaySchema.optional(),
    startTime: timeOfDaySchema.optional(),
    endTime: timeOfDaySchema.optional(),
    userId: idSchema.nullable().optional(),
});
// -------------------------------------------------------- imported feeds --
export const calendarFeedDtoSchema = z.object({
    id: idSchema,
    label: z.string(),
    /** The URL is a bearer secret: only host + last four characters, e.g. `calendar.google.com …a1b2`. */
    urlHint: z.string(),
    enabled: z.boolean(),
    lastSyncedAt: z.iso.datetime().nullable(),
    lastError: z.string().nullable(),
    /** Busy occurrences currently stored from this feed. */
    eventCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
});
export const calendarFeedsResponseSchema = z.object({ feeds: z.array(calendarFeedDtoSchema) });
/** POST /api/v1/calendar/feeds — `webcal://` is accepted and read as `https://`. */
export const createCalendarFeedRequestSchema = z.object({
    label: z.string().trim().min(1).max(120),
    url: z.string().trim().min(1).max(2048),
});
/** PATCH /api/v1/calendar/feeds/:id */
export const updateCalendarFeedRequestSchema = z.object({
    label: z.string().trim().min(1).max(120).optional(),
    enabled: z.boolean().optional(),
});
/** GET /api/v1/calendar/busy?from=&to= (ISO instants; at most 62 days). */
export const busyQuerySchema = z.object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    /** All-day events are usually "out of office" markers, not cooking conflicts; off by default. */
    includeAllDay: z.enum(['true', 'false']).default('false'),
});
export const busyIntervalDtoSchema = z.object({
    start: z.iso.datetime(),
    end: z.iso.datetime(),
    label: z.string(),
    source: z.enum(['calendar', 'routine']),
    allDay: z.boolean(),
});
export const busyResponseSchema = z.object({ intervals: z.array(busyIntervalDtoSchema) });
// ------------------------------------------------------------ export feed --
/** GET /api/v1/calendar/export-token */
export const exportStatusResponseSchema = z.object({ enabled: z.boolean() });
/** POST /api/v1/calendar/export-token — the full URL is only ever returned here. */
export const exportTokenResponseSchema = z.object({
    /** `https://…/api/v1/calendar/feed/<token>.ics` */
    url: z.string(),
    /** Same feed as `webcal://…`, for Apple Calendar and other subscribers. */
    webcalUrl: z.string(),
    /** Google Calendar "add by URL" link. */
    googleUrl: z.string(),
});
// ------------------------------------------------------------- reminders --
export const REMINDER_KINDS = ['thaw', 'custom'];
export const reminderKindSchema = z.enum(REMINDER_KINDS);
export const reminderDtoSchema = z.object({
    id: idSchema,
    mealPlanId: idSchema,
    kind: reminderKindSchema,
    ingredientId: idSchema.nullable(),
    text: z.string(),
    remindAt: z.iso.datetime(),
    doneAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const remindersResponseSchema = z.object({ reminders: z.array(reminderDtoSchema) });
/** POST /api/v1/plans/:id/reminders */
export const createReminderRequestSchema = z.object({
    kind: reminderKindSchema.default('custom'),
    ingredientId: idSchema.nullable().optional(),
    text: z.string().trim().min(1).max(200),
    remindAt: z.iso.datetime({ offset: true }),
});
/** PATCH /api/v1/plans/:id/reminders/:reminderId */
export const updateReminderRequestSchema = z.object({
    text: z.string().trim().min(1).max(200).optional(),
    remindAt: z.iso.datetime({ offset: true }).optional(),
    done: z.boolean().optional(),
});
export const suggestedReminderDtoSchema = z.object({
    ingredientId: idSchema,
    name: z.string(),
    text: z.string(),
    remindAt: z.iso.datetime(),
    /** In the household's freezer: pre-selected; other ingredients are offered too. */
    inFreezer: z.boolean(),
    /** The lead time fell in quiet hours (22:00–07:00) and was moved to 20:00. */
    adjusted: z.boolean(),
});
/** GET /api/v1/plans/:id/reminders/suggested */
export const suggestedRemindersResponseSchema = z.object({
    serveAt: z.iso.datetime(),
    leadHours: z.number().int(),
    suggestions: z.array(suggestedReminderDtoSchema),
});
/** GET /api/v1/reminders/upcoming?days=2 */
export const upcomingRemindersQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(14).default(2),
});
export const upcomingReminderDtoSchema = reminderDtoSchema.extend({
    plan: z.object({
        id: idSchema,
        date: dateOnlySchema,
        slot: mealSlotSchema,
        recipeTitles: z.array(z.string()),
    }),
});
export const upcomingRemindersResponseSchema = z.object({
    reminders: z.array(upcomingReminderDtoSchema),
});
//# sourceMappingURL=schedule.js.map